/**
 * Case study detail from the practice test API may list `question_ids`; the
 * full list from getQuestionByCaseStudy should be restricted to those ids.
 * - `question_ids` missing/null: return full list (legacy payloads).
 * - `question_ids` empty: no questions selected.
 * - otherwise: keep only matching questions, in `question_ids` order.
 */
export function filterCaseStudyQuestionsByQuestionIds<
  T extends { question_id: number },
>(list: T[], questionIds: number[] | undefined | null): T[] {
  if (questionIds == null) return list
  if (questionIds.length === 0) return []
  const byId = new Map<number, T>()
  for (const q of list) {
    byId.set(Number(q.question_id), q)
  }
  const out: T[] = []
  for (const id of questionIds) {
    const row = byId.get(Number(id))
    if (row) out.push(row)
  }
  return out
}

/**
 * Question ids to persist for a case study row from test detail or hydrated state.
 * Prefer filtering hydrated `items`/`questions` by `question_ids` when both exist.
 * If there are no hydrated items yet, use `question_ids` from the API payload.
 */
export function getCaseStudyQuestionIdsFromPayload(cs: {
  items?: Array<{ question_id: number }>
  questions?: Array<{ question_id: number }>
  question_ids?: number[] | null
  [key: string]: unknown
}): number[] {
  const rawItems = Array.isArray(cs.items)
    ? cs.items
    : Array.isArray(cs.questions)
      ? cs.questions
      : []
  if (cs.question_ids != null && Array.isArray(cs.question_ids)) {
    if (cs.question_ids.length === 0) return []
    if (rawItems.length > 0) {
      const filtered = filterCaseStudyQuestionsByQuestionIds(
        rawItems,
        cs.question_ids
      )
      return filtered
        .map((q) => Number(q.question_id))
        .filter((id) => Number.isFinite(id))
    }
    return cs.question_ids
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
  }
  return rawItems
    .map((item) => Number(item.question_id))
    .filter((id) => Number.isFinite(id))
}
